/**
 * Helpers for recovering from an Auth0 session that can no longer be used.
 *
 * The SPA SDK keeps the user profile and the access token in localStorage.
 * When the token expires and cannot be renewed silently, ProtectedRoute sends
 * the browser through Auth0's /authorize once with the current location as
 * `returnTo`; the Auth0 session cookie normally makes that round trip
 * invisible. The attempt is stamped in sessionStorage so that, if the app
 * comes straight back and the session is still unusable (for example the
 * backend keeps rejecting freshly issued tokens), the next failure inside the
 * window falls back to a full logout instead of redirecting forever.
 */

const SILENT_REAUTH_ATTEMPT_KEY = "proda_auth_silent_reauth_at";

/** A second failure within this window after a silent re-auth is a loop. */
export const SILENT_REAUTH_WINDOW_MS = 60_000;

function readLastAttempt(): number | null {
    try {
        const raw = window.sessionStorage.getItem(SILENT_REAUTH_ATTEMPT_KEY);
        if (raw === null) {
            return null;
        }
        const value = Number(raw);
        return Number.isFinite(value) ? value : null;
    } catch {
        return null;
    }
}

/**
 * True when no silent re-auth was attempted recently, so another one is safe.
 */
export function shouldAttemptSilentReauth(now: number = Date.now()): boolean {
    const last = readLastAttempt();
    return last === null || now - last > SILENT_REAUTH_WINDOW_MS;
}

/** Record that a silent re-auth round trip is about to start. */
export function markSilentReauthAttempt(now: number = Date.now()): void {
    try {
        window.sessionStorage.setItem(SILENT_REAUTH_ATTEMPT_KEY, String(now));
    } catch {
        // Storage unavailable (private mode, blocked site data): the loop guard
        // degrades to "always redirect", which is the pre-existing behaviour.
    }
}

/** Forget the last attempt, e.g. after a deliberate logout. */
export function clearSilentReauthAttempt(): void {
    try {
        window.sessionStorage.removeItem(SILENT_REAUTH_ATTEMPT_KEY);
    } catch {
        // Nothing to clear when storage is unavailable.
    }
}

/** The in-app location to come back to after the Auth0 round trip. */
export function currentReturnTo(): string {
    const { pathname, search, hash } = window.location;
    return `${pathname}${search}${hash}`;
}

/** Short, log-friendly description of an Auth0 SDK or fetch failure. */
export function describeAuthError(err: unknown): string {
    if (err !== null && typeof err === "object") {
        const code = (err as { error?: unknown }).error;
        if (typeof code === "string" && code !== "") {
            return code;
        }
    }
    return err instanceof Error ? err.message : String(err);
}
